import { useEffect, useState } from "react";
import { useRobots } from "../../../hooks/robot";
import { RobotType } from "../../../hooks/robot/index.type";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip } from "@mui/material";
import { defaultHeaderStyle } from "../../robots/components/v2/_style";
import { COLOR } from "../../../utils/colors";
import swiftbot from '../../../assets/swiftbot.png';
import { dummyRobotTableData } from "../../robots/components/v2/_const";
import { underscoreToSpace } from "../../../utils/utils";
import { useConfigListRobot, useUnassignRobotMutation } from "../../../hooks/config-page/robot";
import { useNavigate } from "react-router-dom";

const columns = ['Preview', 'Robot Name', 'Robot Type', 'Serial No', 'Mac Address', 'Organisation', 'Site', 'Customer', 'Customer Contact', 'Action']

export default function ListRobotTable({
  selectable = false,
  onSelectedChange,
  defaultSelectedRobots,
  search,
  onEditRobot
}: {
  selectable?: boolean,
  onSelectedChange?: (selecteds: RobotType[]) => void,
  search?: string,
  defaultSelectedRobots?: RobotType[],
  onEditRobot?: (robot: RobotType) => void
}) {
  const navigate = useNavigate()
  // const { data: dataRobot, refetch, isLoading } = useRobots({ search: search || '' })
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [selectedRobots, setSelectedRobots] = useState<RobotType[]>(defaultSelectedRobots || [])
  const { data: dataRobot, refetch, isLoading } = useConfigListRobot({ search })

  const [selectedToUnassign, setSelectedToUnassign] = useState<RobotType>()

  const { trigger: unassignRobot } = useUnassignRobotMutation({
    id: selectedToUnassign?.robot_mac || '',
    onSuccess: () => {
      setSelectedToUnassign(undefined)
      refetch()
    }
  })

  useEffect(() => {
    refetch()
  }, [search, page, rowsPerPage])

  const robots = dataRobot?.robots || []
  const itemCount = dataRobot?.robots.length || 1

  const handleChangePage = (newPage: number) => {
    setPage(newPage + 1)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(1)
  }

  const isSelected = (robot: RobotType) => {
    return selectedRobots.find((r) => r.robot_mac === robot.robot_mac) !== undefined
  }

  const isDisabled = (index: number) => {
    return false
  }

  const handleSelectRobot = (robot: RobotType) => {
    if (isSelected(robot)) {
      const updatedSelectedRobots = selectedRobots.filter((r) => r.robot_mac !== robot.robot_mac)
      setSelectedRobots(updatedSelectedRobots)
      onSelectedChange?.(updatedSelectedRobots)
      return
    }
    const newSelectedRobots = [...selectedRobots, robot]
    setSelectedRobots(newSelectedRobots)
    onSelectedChange?.(newSelectedRobots)
  }

  const handleUnassign = (robot?: RobotType) => {
    if (!robot) {
      return
    }

    unassignRobot()
    setSelectedToUnassign(undefined)
  }

  return (
    <Paper elevation={2} sx={{ display: 'flex', flexDirection: 'column', maxHeight: "67vh", borderRadius: "10px", border: "1px solid #E0E0E0", m: '20px auto' }}>
      <TableContainer sx={{ backgroundColor: "#F5F5F5", alignItems: "center", overflowX: "auto" }}>
        <Table stickyHeader>
          <TableHead sx={{ width: "100%", margin: "auto" }}>
            <TableRow sx={{ width: "100%", margin: "auto" }}>
              {selectable && (
                <TableCell width={80} align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>
                </TableCell>
              )}
              {columns.map((column, index) => (
                <TableCell key={index} align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {robots.map((robot, index) => (
              <TableRow
                key={index}
                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: COLOR.gray[100] } }}
                onClick={() => {
                  if (isDisabled(index)) {
                    return
                  }
                  handleSelectRobot(robot)
                }}>
                {selectable && (
                  <TableCell align="center" sx={{ ...defaultHeaderStyle, width: '80px' }}>
                    <Tooltip title={isDisabled(index) ? "This robot is already assigned" : ""} arrow>
                      <Box>
                        <Checkbox
                          checked={isSelected(robot)}
                          onChange={() => handleSelectRobot(robot)}
                          disabled={isDisabled(index)}
                        />
                      </Box>
                    </Tooltip>
                  </TableCell>
                )}
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>
                  <img src={swiftbot} alt={robot.robot_name} style={{ width: '50px', height: '50px', objectFit: 'scale-down' }} />
                </TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{robot.robot_name}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle, textTransform: 'capitalize' }}>{underscoreToSpace(robot.robot_type)}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{robot.robot_serial_number}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{robot.robot_mac}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{dummyRobotTableData.organisation_name}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{dummyRobotTableData.site}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{dummyRobotTableData.customer_name}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>{dummyRobotTableData.customer_contact}</TableCell>
                <TableCell align="center" sx={{ ...defaultHeaderStyle }}>
                  <Box sx={{ display: 'flex', gap: '6px', justifyContent: 'center', alignItems: 'center' }}>
                    <Button onClick={() => onEditRobot?.(robot)} variant="outlined">
                      Modify
                    </Button>
                    {robot.has_map_assigned && (
                      <Button onClick={() => setSelectedToUnassign(robot)} variant="outlined" color="error" >
                        Unassign
                      </Button>
                    )
                    }
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {robots.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: COLOR.gray[500] }}>
                  No Selected Layout Maps data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={itemCount}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onPageChange={(e, num) => handleChangePage(num)}
        onRowsPerPageChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value || '10'))}
        sx={{ flexShrink: 0, boxShadow: "none" }} // Prevent pagination from shrinking
      />

      <Dialog
        open={selectedToUnassign !== undefined}
        onClose={() => setSelectedToUnassign(undefined)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure want to unassign this Robot from all robot maps?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This action will unassign the selected Robot from all robot maps and can't be undone. Do you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedToUnassign(undefined)} autoFocus>Cancel</Button>
          <Button onClick={() => handleUnassign(selectedToUnassign)} variant="contained" color="error">
            Yes, Unassign
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )

}